import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import AppButton from '../../components/AppButton/AppButton';
import AppSelect from '../../components/AppSelect/AppSelect';
import { useSelector, useDispatch } from 'react-redux';
import AppInput from '../../components/AppInput/AppInput';
import TextArea from '../../components/AppInput/TextArea';
import { User, Mail, Phone, MapPin, FileText, Building } from 'lucide-react';
import { stateOptions } from '../../constant/Options';
import { EMAIL_REGEX } from '../../constant/regex';
import { getSelectedUnit } from '../../storage/Storage';
import { addVendor, updateVendor, searchVendor } from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import SearchableSelect from '../../components/AppSelect/SearchableSelect';
import { VendorValidationSchema } from '../../validationscheema/VenderValidation';
import { getErrorMsg, hasError } from '../../utils/utils';
import toast from 'react-hot-toast';

function AddVendor() {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const url = window.location.pathname;
    const urlId = url.split('/')[3];

    useEffect(() => {
        const initialVendorData = {
            vendorName: "",
            gst: "",
            address: "",
            state: "",
            contact: "",
            email: "",
        };

        if (urlId) {
            setIsEditMode(true);
            loadData();
        } else {
            dispatch(setApiJson(initialVendorData));
            dispatch(setApiErrorJson({}));
        }
    }, [dispatch, urlId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const json = {
                page: 1,
                limit: 10,
                search: {
                    _id: urlId,
                    unitIds: getSelectedUnit()?._id
                }
            };

            const res = await HitApi(json, searchVendor);

            if (res.statusCode === 200) {
                const vendorData = res?.data?.docs?.[0];

                if (vendorData) {
                    dispatch(setApiJson(vendorData));
                    dispatch(setApiErrorJson({}));
                } else {
                    toast.error('Vendor not found');
                }
            } else {
                toast.error('Failed to load vendor data');
            }
        } catch (error) {
            toast.error('Error loading vendor data. Please try again.');
        } finally {
            setLoading(false);
        }
    };



    const handleSubmit = () => {
        setSubmitLoading(true);

        VendorValidationSchema(ApiReducer?.apiJson).then((errors) => {
            dispatch(setApiErrorJson(errors));

            if (Object.keys(errors).length === 0) {
                const vendorData = {
                    ...ApiReducer.apiJson,
                    unitIds: getSelectedUnit()?._id
                };

                if (isEditMode) {
                    vendorData._id = urlId;

                    HitApi(vendorData, updateVendor).then((res) => {
                        setSubmitLoading(false);
                        if (res?.statusCode === 200) {
                            toast.success('Vendor updated successfully!');
                            setTimeout(() => {
                                navigate('/vendormanagement');
                            }, 1000);
                        } else {
                            toast.error(res?.message || "Failed to update vendor. Please try again.");
                        }
                    }).catch((error) => {
                        setSubmitLoading(false);
                        toast.error("Error updating vendor. Please try again.");
                    });
                } else {
                    HitApi(vendorData, addVendor).then((res) => {
                        setSubmitLoading(false);
                        if (res?.message === "Vendor created successfully" || res?.success) {
                            toast.success('Vendor created successfully!');
                            setTimeout(() => {
                                navigate('/vendormanagement');
                            }, 1000);
                        } else {
                            toast.error(res?.message || "Failed to create vendor. Please try again.");
                        }
                    }).catch((error) => {
                        setSubmitLoading(false);
                        toast.error("Error creating vendor. Please try again.");
                    });
                }
            } else {
                setSubmitLoading(false);
            }
        }).catch((error) => {
            setSubmitLoading(false);
        });
    };

    if (loading && isEditMode) {
        return (
            <div className='p-5'>
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading vendor data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={isEditMode ? 'Edit Vendor' : 'Add New Vendor'}
                description={isEditMode ? 'Update Vendor Information' : 'Add New Vendor to System'}
            />

            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Vendor Information</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <AppInput
                        title="Vendor Name"
                        placeholder="Enter vendor name"
                        name="vendorName"
                        important={true}
                        error={hasError('vendorName', ApiReducer)}
                        errormsg={getErrorMsg('vendorName', ApiReducer)}
                        icon={<User size={18} />}
                        disabled={submitLoading}
                    />

                    <AppInput
                        title="GST Number"
                        placeholder="Enter GST number"
                        name="gst"
                        important={true}
                        error={hasError('gst', ApiReducer)}
                        errormsg={getErrorMsg('gst', ApiReducer)}
                        icon={<FileText size={18} />}
                        disabled={submitLoading}
                    />

                    <SearchableSelect
                        title="State"
                        name="state"
                        options={stateOptions}
                        important={true}
                        placeholder="Select a state"
                        error={hasError('state', ApiReducer)}
                        errormsg={getErrorMsg('state', ApiReducer)}
                        icon={<MapPin size={18} />}
                        disabled={submitLoading}
                    />

                    <AppInput
                        title="Contact Number"
                        placeholder="Enter contact number"
                        name="contact"
                        important={true}
                        error={hasError('contact', ApiReducer)}
                        errormsg={getErrorMsg('contact', ApiReducer)}
                        icon={<Phone size={18} />}
                        maxLength={10}
                        disabled={submitLoading}
                    />

                    <AppInput
                        title="Email Address"
                        placeholder="Enter email address"
                        name="email"
                        important={true}
                        error={hasError('email', ApiReducer)}
                        errormsg={getErrorMsg('email', ApiReducer)}
                        type="email"
                        icon={<Mail size={18} />}
                        disabled={submitLoading}
                    />
                </div>

                <div className="mt-4">
                    <TextArea
                        title="Address"
                        name="address"
                        placeholder="Enter vendor address..."
                        rows={3}
                        important={true}
                        error={hasError('address', ApiReducer)}
                        errormsg={getErrorMsg('address', ApiReducer)}
                        disabled={submitLoading}
                    />
                </div>

                <div className='mt-5 flex justify-end'>
                    <AppButton
                        buttontext={submitLoading ? "Processing..." : (isEditMode ? "Update Vendor" : "Add Vendor")}
                        onClick={handleSubmit}
                        disabled={submitLoading}
                        isLoading={submitLoading}
                    />
                </div>
            </div>
        </div>
    );
}

export default AddVendor;